import { useState } from 'react';
import {
  Button,
  Checkbox,
  Collapse,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import type { EtrnDetails } from '@expeditor/shared';
import { EtrnDateField, EtrnDateTimeField } from './EtrnDateFields';

type Vehicle = NonNullable<EtrnDetails['vehicle']>;
const undef = (s: string) => s || undefined;

/** Тип владения ТС (ТипВлад ЭТрН). */
export const OWNERSHIP_TYPE_OPTIONS = [
  { value: '1', label: '1 — Собственность' },
  { value: '2', label: '2 — Совместная собственность супругов' },
  { value: '3', label: '3 — Аренда' },
  { value: '4', label: '4 — Лизинг' },
  { value: '5', label: '5 — Безвозмездное пользование' },
];
/** Метод определения массы груза (МетОпрМасс ЭТрН). */
export const MASS_METHOD_OPTIONS = [
  { value: '01', label: '01 — Взвешивание по общей массе' },
  { value: '02', label: '02 — Взвешивание поосно' },
  { value: '03', label: '03 — Расчётная масса груза' },
];

/** Сворачиваемая подсекция. */
export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Stack gap={4}>
      <Button
        variant="subtle"
        size="compact-xs"
        justify="flex-start"
        leftSection={open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        onClick={() => setOpen((o) => !o)}
        style={{ alignSelf: 'flex-start' }}
      >
        {title}
      </Button>
      <Collapse in={open}>
        <Stack gap="xs" pl="sm">
          {children}
        </Stack>
      </Collapse>
    </Stack>
  );
}

/** Реквизиты ТС/прицепа (в свёрнутой секции). */
export function VehicleFields({
  title,
  value,
  onChange,
}: {
  title: string;
  value: Vehicle;
  onChange: (v: Vehicle) => void;
}) {
  const veh = value ?? {};
  const set = (p: Partial<Vehicle>) => onChange({ ...veh, ...p });
  const lease = veh.lease ?? {};
  const setLease = (p: Partial<NonNullable<Vehicle['lease']>>) => set({ lease: { ...lease, ...p } });
  return (
    <Section title={title}>
      <Group grow>
        <TextInput label="СТС (номер)" value={veh.stsNumber ?? ''} onChange={(e) => set({ stsNumber: undef(e.currentTarget.value) })} />
        <TextInput label="VIN" value={veh.vin ?? ''} onChange={(e) => set({ vin: undef(e.currentTarget.value) })} />
        <TextInput label="Гос. номер" value={veh.regNumber ?? ''} onChange={(e) => set({ regNumber: undef(e.currentTarget.value) })} />
      </Group>
      <Group grow>
        <TextInput label="Тип (напр. Седельный тягач)" value={veh.kind ?? ''} onChange={(e) => set({ kind: undef(e.currentTarget.value) })} />
        <TextInput label="Марка" value={veh.brand ?? ''} onChange={(e) => set({ brand: undef(e.currentTarget.value) })} />
      </Group>
      <Group grow>
        <TextInput label="Грузоподъёмность, т" value={veh.capacityTons ?? ''} onChange={(e) => set({ capacityTons: undef(e.currentTarget.value) })} />
        <TextInput label="Вместимость, м³" value={veh.volume ?? ''} onChange={(e) => set({ volume: undef(e.currentTarget.value) })} />
        <Select label="Тип владения" placeholder="выберите" data={OWNERSHIP_TYPE_OPTIONS} value={veh.ownershipType ?? null} onChange={(v) => set({ ownershipType: v ?? undefined })} clearable />
      </Group>
      <Text size="xs" c="dimmed">
        Основание владения (аренда/лизинг)
      </Text>
      <Group grow>
        <TextInput label="Документ" placeholder="Договор лизинга" value={lease.docName ?? ''} onChange={(e) => setLease({ docName: undef(e.currentTarget.value) })} />
        <TextInput label="Номер" value={lease.docNumber ?? ''} onChange={(e) => setLease({ docNumber: undef(e.currentTarget.value) })} />
        <EtrnDateField label="Дата" value={lease.docDate ?? ''} onChange={(v) => setLease({ docDate: v || undefined })} />
        <TextInput label="ИНН владельца" value={lease.ownerInn ?? ''} onChange={(e) => setLease({ ownerInn: undef(e.currentTarget.value) })} />
      </Group>
    </Section>
  );
}

/**
 * Данные транспортного средства: водитель (ФИО/телефон/ВУ/ИНН), ТС и прицеп.
 * ФИО и телефон водителя живут в основных полях документа, поэтому приходят
 * отдельными пропами.
 */
export function TransportEtrnFields({
  value,
  onChange,
  driverName,
  setDriverName,
  driverPhone,
  setDriverPhone,
}: {
  value: EtrnDetails;
  onChange: (v: EtrnDetails) => void;
  driverName: string;
  setDriverName: (v: string) => void;
  driverPhone: string;
  setDriverPhone: (v: string) => void;
}) {
  const driver = value.driver ?? {};
  const setDriver = (p: Partial<NonNullable<EtrnDetails['driver']>>) =>
    onChange({ ...value, driver: { ...driver, ...p } });
  return (
    <Stack gap="xs">
      <Text size="xs" fw={500}>
        Водитель
      </Text>
      <Group grow>
        <TextInput label="ФИО водителя" value={driverName} onChange={(e) => setDriverName(e.currentTarget.value)} />
        <TextInput label="Телефон водителя" value={driverPhone} onChange={(e) => setDriverPhone(e.currentTarget.value)} />
      </Group>
      <Group grow>
        <TextInput label="Серия ВУ" value={driver.licenseSeries ?? ''} onChange={(e) => setDriver({ licenseSeries: undef(e.currentTarget.value) })} />
        <TextInput label="Номер ВУ" value={driver.licenseNumber ?? ''} onChange={(e) => setDriver({ licenseNumber: undef(e.currentTarget.value) })} />
        <EtrnDateField label="Дата выдачи ВУ" value={driver.licenseDate ?? ''} onChange={(v) => setDriver({ licenseDate: v || undefined })} />
        <TextInput label="ИНН водителя" value={driver.inn ?? ''} onChange={(e) => setDriver({ inn: undef(e.currentTarget.value) })} />
      </Group>
      <VehicleFields title="Транспортное средство (тягач)" value={value.vehicle ?? {}} onChange={(veh) => onChange({ ...value, vehicle: veh })} />
      <VehicleFields title="Прицеп" value={value.trailer ?? {}} onChange={(veh) => onChange({ ...value, trailer: veh })} />
    </Stack>
  );
}

/** Груз: опасный груз, габариты, пломбы и указания. */
export function CargoEtrnFields({
  value,
  onChange,
}: {
  value: EtrnDetails;
  onChange: (v: EtrnDetails) => void;
}) {
  const patch = (p: Partial<EtrnDetails>) => onChange({ ...value, ...p });
  const dg = value.dangerous ?? {};
  const setDg = (p: Partial<NonNullable<EtrnDetails['dangerous']>>) => patch({ dangerous: { ...dg, ...p } });
  const dim = value.dimensions ?? {};
  const setDim = (p: Partial<NonNullable<EtrnDetails['dimensions']>>) => patch({ dimensions: { ...dim, ...p } });
  return (
    <>
      <Section title="Опасный груз">
        <Group grow>
          <TextInput label="№ ООН" value={dg.unNumber ?? ''} onChange={(e) => setDg({ unNumber: undef(e.currentTarget.value) })} />
          <TextInput label="Класс" value={dg.classId ?? ''} onChange={(e) => setDg({ classId: undef(e.currentTarget.value) })} />
          <TextInput label="Классиф. код" value={dg.classCode ?? ''} onChange={(e) => setDg({ classCode: undef(e.currentTarget.value) })} />
          <TextInput label="Группа упаковки" value={dg.packingGroup ?? ''} onChange={(e) => setDg({ packingGroup: undef(e.currentTarget.value) })} />
        </Group>
        <TextInput label="Надлежащее отгрузочное наименование" value={dg.shippingName ?? ''} onChange={(e) => setDg({ shippingName: undef(e.currentTarget.value) })} />
        <Group grow>
          <TextInput label="Знак опасности" value={dg.dangerSign ?? ''} onChange={(e) => setDg({ dangerSign: undef(e.currentTarget.value) })} />
          <TextInput label="Код огр. через тоннели" value={dg.tunnelCode ?? ''} onChange={(e) => setDg({ tunnelCode: undef(e.currentTarget.value) })} />
        </Group>
      </Section>

      <Section title="Габариты места, м">
        <Group grow>
          <TextInput label="Высота" value={dim.height ?? ''} onChange={(e) => setDim({ height: undef(e.currentTarget.value) })} />
          <TextInput label="Длина" value={dim.length ?? ''} onChange={(e) => setDim({ length: undef(e.currentTarget.value) })} />
          <TextInput label="Ширина" value={dim.width ?? ''} onChange={(e) => setDim({ width: undef(e.currentTarget.value) })} />
        </Group>
      </Section>

      <Section title="Пломбы и указания">
        <TextInput label="Пломбы" placeholder="Пломба F6925970 …" value={value.seals ?? ''} onChange={(e) => patch({ seals: undef(e.currentTarget.value) })} />
        <Group grow>
          <Checkbox label="Запрет перегрузки" checked={value.noReload ?? false} onChange={(e) => patch({ noReload: e.currentTarget.checked })} />
          <TextInput label="Указания о нормах провоза" value={value.transportNorms ?? ''} onChange={(e) => patch({ transportNorms: undef(e.currentTarget.value) })} />
        </Group>
      </Section>
    </>
  );
}

/** Договор перевозки / транспортной экспедиции (ТЭО) — отдельный блок. */
export function ContractEtrnFields({
  value,
  onChange,
}: {
  value: EtrnDetails;
  onChange: (v: EtrnDetails) => void;
}) {
  const patch = (p: Partial<EtrnDetails>) => onChange({ ...value, ...p });
  const contract = value.contract ?? {};
  const setContract = (p: Partial<NonNullable<EtrnDetails['contract']>>) =>
    patch({ contract: { ...contract, ...p } });
  return (
    <Section title="Договор перевозки/ТЭ (ТЭО)">
      <Group grow>
        <TextInput label="Наименование" placeholder="Договор транспортной экспедиции" value={contract.docName ?? ''} onChange={(e) => setContract({ docName: undef(e.currentTarget.value) })} />
        <TextInput label="Номер" value={contract.docNumber ?? ''} onChange={(e) => setContract({ docNumber: undef(e.currentTarget.value) })} />
        <EtrnDateField label="Дата" value={contract.docDate ?? ''} onChange={(v) => setContract({ docDate: v || undefined })} />
      </Group>
    </Section>
  );
}

/** Факт погрузки. */
export function LoadingContractEtrnFields({
  value,
  onChange,
}: {
  value: EtrnDetails;
  onChange: (v: EtrnDetails) => void;
}) {
  const patch = (p: Partial<EtrnDetails>) => onChange({ ...value, ...p });
  const ld = value.loading ?? {};
  const setLd = (p: Partial<NonNullable<EtrnDetails['loading']>>) => patch({ loading: { ...ld, ...p } });
  const infra = ld.infraOwner ?? { type: null, name: '' };
  return (
    <>
      <Section title="Факт погрузки">
        <EtrnDateTimeField label="Плановое время подачи под погрузку" value={ld.plannedAt ?? ''} onChange={(v) => setLd({ plannedAt: v || undefined })} />
        <Group grow>
          <EtrnDateTimeField label="Прибытие (дата/время)" value={ld.arrivalAt ?? ''} onChange={(v) => setLd({ arrivalAt: v || undefined })} />
          <EtrnDateTimeField label="Убытие (дата/время)" value={ld.departureAt ?? ''} onChange={(v) => setLd({ departureAt: v || undefined })} />
        </Group>
        <Group grow>
          <TextInput label="Масса брутто отгруж., кг" value={ld.massBrutto ?? ''} onChange={(e) => setLd({ massBrutto: undef(e.currentTarget.value) })} />
          <Select label="Метод определения массы" placeholder="выберите" data={MASS_METHOD_OPTIONS} value={ld.massMethod ?? null} onChange={(v) => setLd({ massMethod: v ?? undefined })} clearable />
          <TextInput label="Кол-во мест при приёме" value={ld.placesAccepted ?? ''} onChange={(e) => setLd({ placesAccepted: undef(e.currentTarget.value) })} />
        </Group>
        <Checkbox
          label="Лицо, ответственное за погрузку (ЛОП), совпадает с грузоотправителем"
          checked={ld.personSameAsShipper ?? false}
          onChange={(e) => setLd({ personSameAsShipper: e.currentTarget.checked })}
        />
        <Group grow>
          <TextInput label="Должность лица погрузки" value={ld.personPost ?? ''} onChange={(e) => setLd({ personPost: undef(e.currentTarget.value) })} />
          <TextInput label="ФИО лица погрузки" value={ld.personName ?? ''} onChange={(e) => setLd({ personName: undef(e.currentTarget.value) })} />
        </Group>
        <TextInput
          label="Основание полномочий на погрузку (должностные обязанности)"
          placeholder="Должностные обязанности"
          value={ld.personBasis ?? ''}
          onChange={(e) => setLd({ personBasis: undef(e.currentTarget.value) })}
        />
        <Text size="xs" c="dimmed">
          Владелец инфраструктуры места погрузки (при заполнении обязателен телефон)
        </Text>
        <Group grow>
          <TextInput label="Наименование" value={infra.name} onChange={(e) => setLd({ infraOwner: { ...infra, name: e.currentTarget.value } })} />
          <TextInput label="ИНН" value={infra.inn ?? ''} onChange={(e) => setLd({ infraOwner: { ...infra, inn: e.currentTarget.value || null } })} />
          <TextInput label="КПП" value={infra.kpp ?? ''} onChange={(e) => setLd({ infraOwner: { ...infra, kpp: e.currentTarget.value || null } })} />
        </Group>
        <Group grow>
          <TextInput label="Телефон" value={infra.phone ?? ''} onChange={(e) => setLd({ infraOwner: { ...infra, phone: e.currentTarget.value || null } })} />
          <TextInput label="Адрес" value={infra.address ?? ''} onChange={(e) => setLd({ infraOwner: { ...infra, address: e.currentTarget.value || null } })} />
        </Group>
      </Section>
    </>
  );
}

type Mark = NonNullable<EtrnDetails['marks']>[number];

/**
 * Блок 11 — отметки грузоотправителей/грузополучателей/перевозчиков (ОтметГО).
 * Список отметок: дата/время + обстоятельства; опционально реквизиты акта.
 */
export function MarksEtrnFields({
  value,
  onChange,
}: {
  value: EtrnDetails;
  onChange: (v: EtrnDetails) => void;
}) {
  const marks = value.marks ?? [];
  const setMarks = (next: Mark[]) => onChange({ ...value, marks: next.length ? next : undefined });
  const update = (i: number, p: Partial<Mark>) =>
    setMarks(marks.map((m, idx) => (idx === i ? { ...m, ...p } : m)));
  const add = () => setMarks([...marks, {}]);
  const remove = (i: number) => setMarks(marks.filter((_, idx) => idx !== i));

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Отметки о ходе перевозки: повреждения, замечания, коммерческие акты и т.п.
        Пишутся в XML как элементы ОтметГО.
      </Text>
      {marks.map((m, i) => (
        <Stack key={i} gap="xs" style={{ borderLeft: '2px solid var(--mantine-color-gray-3)', paddingLeft: 8 }}>
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              Отметка {i + 1}
            </Text>
            <Button size="compact-xs" variant="subtle" color="red" onClick={() => remove(i)}>
              Удалить
            </Button>
          </Group>
          <EtrnDateTimeField
            label="Дата и время отметки"
            value={m.dateTime ?? ''}
            onChange={(v) => update(i, { dateTime: v || undefined })}
          />
          <Textarea
            label="Обстоятельства отметки"
            placeholder="Например: при погрузке обнаружено повреждение упаковки"
            autosize
            minRows={2}
            maxRows={5}
            value={m.circumstances ?? ''}
            onChange={(e) => update(i, { circumstances: undef(e.currentTarget.value) })}
          />
          <Section title="Реквизиты акта (необязательно)">
            <Group grow>
              <TextInput
                label="Наименование акта"
                value={m.actName ?? ''}
                onChange={(e) => update(i, { actName: undef(e.currentTarget.value) })}
              />
              <TextInput
                label="Номер акта"
                value={m.actNumber ?? ''}
                onChange={(e) => update(i, { actNumber: undef(e.currentTarget.value) })}
              />
            </Group>
            <Group grow>
              <EtrnDateField
                label="Дата акта"
                value={m.actDate ?? ''}
                onChange={(v) => update(i, { actDate: v || undefined })}
              />
              <TextInput
                label="ИНН составителя акта"
                description="Обязателен, если заполнен акт"
                value={m.actOwnerInn ?? ''}
                onChange={(e) => update(i, { actOwnerInn: undef(e.currentTarget.value) })}
              />
            </Group>
          </Section>
        </Stack>
      ))}
      <Button size="xs" variant="light" onClick={add} style={{ alignSelf: 'flex-start' }}>
        + Добавить отметку
      </Button>
    </Stack>
  );
}
